import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { READ_ONLY_ROLES } from '@dayon/rbac';

import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';

/**
 * Saved views — a named filter set an operator comes back to.
 *
 * ONE COMPONENT FOR EVERY SCREEN, because a view is the same object on all of
 * them: a name and the screen's own query string. The dialog is handed the
 * screen it belongs to and the current query string, and hands back the query
 * string of whichever view was opened. It knows nothing about what any
 * particular filter means, which is what lets a screen gain a chip without this
 * file changing.
 *
 * The Transactions module drew this dialog from the day it shipped with an empty
 * state saying no preference table existed. It does now
 * (`packages/db/sql/720_admin_saved_view.sql`), and the design's own promise is
 * kept: a shared view is READ-ONLY to everybody but its owner, which the row
 * states rather than discovering on a refused save.
 */
export type SavedViewScreen = 'TRANSACTIONS' | 'WITHDRAWALS' | 'PLAYERS' | 'GAMES';

interface SavedView {
  id: string;
  name: string;
  query: string;
  shared: boolean;
  updated_at: string;
  /** True when this session's admin owns it — the only one who may change it. */
  mine: boolean;
  owner: string;
}

const stamp = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Asia/Manila',
});

/**
 * How many filters a stored view carries, for the row beneath its name.
 *
 * Counted rather than named: this component does not know what `kind` or
 * `playerId` mean on any given screen, and inventing labels for them here is how
 * a shared component starts carrying a copy of four screens' vocabularies.
 */
function filterCount(query: string): number {
  return query === '' ? 0 : new URLSearchParams(query).size;
}

export function SavedViewsDialog({
  open,
  screen,
  screenLabel,
  currentQuery,
  onClose,
  onApply,
}: {
  open: boolean;
  screen: SavedViewScreen;
  /** How this screen is named to an operator, e.g. "Transactions". */
  screenLabel: string;
  /** The query string as it stands, without the leading '?'. */
  currentQuery: string;
  onClose: () => void;
  onApply: (query: string) => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  /*
   * A READ-ONLY ROLE KEEPS ITS VIEWS TO ITSELF: the save route refuses a shared one from the
   * Auditor or the PAGCOR Regulator (READ_ONLY_ROLES, matched by name as everywhere), because a
   * shared view is seen by every staff member on its screen. So the box is not offered to them, and
   * the dialog says why.
   */
  const { session } = useSession();
  const role = session?.role ?? null;
  const readOnly = role !== null && (READ_ONLY_ROLES as readonly string[]).includes(role);

  const [views, setViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(true);
  /* Did the read FAIL, or succeed and return nothing? An empty list under a
     dismissed error dialog reads as "you have saved no views", which is a
     statement this component cannot make when nothing came back. */
  const [loadFailed, setLoadFailed] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [writes, setWrites] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api
      .get<{ items: SavedView[] }>(`/api/views?screen=${screen}`)
      .then((d) => {
        if (cancelled) return;
        setViews(d.items);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setViews([]);
        setLoadFailed(true);
        showError({
          title: 'Could not load your saved views',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, screen, showError, writes]);

  useEffect(() => {
    if (open) {
      setName('');
      setShared(false);
      setFields({});
    }
  }, [open]);

  /** The view whose name matches what is typed — Save would REPLACE it. */
  const clashing = views.find(
    (v) => v.mine && v.name.toLowerCase() === name.trim().toLowerCase(),
  );

  const save = useCallback(async () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      setFields({ name: 'Give the view a name you will recognise later.' });
      return;
    }
    setBusy(true);
    try {
      const result = await api.post<{ name: string; shared: boolean }>('/api/views', {
        screen,
        name: trimmed,
        query: currentQuery,
        shared,
      });
      toast(
        `“${result.name}” saved${result.shared ? ' and shared with every admin who can open this screen' : ''}.`,
      );
      setName('');
      setShared(false);
      setWrites((n) => n + 1);
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'The view was not saved',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was saved. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  }, [name, shared, screen, currentQuery, showError, toast]);

  const remove = useCallback(
    async (view: SavedView) => {
      setBusy(true);
      try {
        await api.post(`/api/views/${view.id}/delete`, {});
        toast(`“${view.name}” removed. The name is free to use again.`);
        setWrites((n) => n + 1);
      } catch (error: unknown) {
        showError({
          title: `“${view.name}” was not removed`,
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'Nothing was changed. Try again shortly.',
          ...(error instanceof ApiRequestError ? { code: error.code } : {}),
        });
      } finally {
        setBusy(false);
      }
    },
    [showError, toast],
  );

  const currentFilters = filterCount(currentQuery);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      wide
      title={`Saved views — ${screenLabel}`}
      actions={<Button onClick={onClose}>Close</Button>}
    >
      <div className="stack">
        {loading && views.length === 0 ? (
          <div className="skeleton" style={{ height: '120px' }}>
            &nbsp;
          </div>
        ) : views.length === 0 ? (
          <EmptyState
            icon={loadFailed ? 'warning-circle' : 'bookmark-simple'}
            title={loadFailed ? 'Your saved views could not be read' : 'No views saved yet'}
          >
            {loadFailed
              ? 'The request did not come back, so this list is empty because nothing was read — not because you have saved nothing. The error dialog carries what the server said; try again shortly.'
              : 'Set the filters you want on the screen behind this dialog, then name them below. A view stores the filters, the search, the date window and the page — everything in the address bar, which is why the same view pastes into a ticket as a link.'}
          </EmptyState>
        ) : (
          <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {views.map((v) => (
              <li key={v.id} className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <strong>{v.name}</strong>{' '}
                  {v.shared ? (
                    <Tag tone="outline">{v.mine ? 'Shared by you' : `Shared by ${v.owner}`}</Tag>
                  ) : null}
                  <div className="text-subtle" style={{ fontSize: '11px' }}>
                    {filterCount(v.query) === 0
                      ? 'no filters — the whole list'
                      : `${filterCount(v.query)} filter${filterCount(v.query) === 1 ? '' : 's'}`}{' '}
                    · saved {stamp.format(new Date(v.updated_at))}
                    {v.mine ? '' : ' · read-only, because you are not its owner'}
                  </div>
                </div>
                <div className="row">
                  <Button
                    small
                    onClick={() => {
                      onApply(v.query);
                      onClose();
                    }}
                  >
                    Open
                  </Button>
                  {/* Only the owner may remove one. A shared view is visible to
                      everybody and changeable by one person, which is what the
                      design means by read-only — so the control is absent rather
                      than disabled, and the line above says why. */}
                  {v.mine ? (
                    <Button small disabled={busy} onClick={() => void remove(v)}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="notice notice-quiet">
          <Icon name="funnel" />
          <div>
            Saving stores <strong>the filters as they are right now</strong> on the screen behind
            this dialog —{' '}
            {currentFilters === 0 ? (
              <>
                and there are none set, so this would save <em>the whole list</em>. That is a
                perfectly good view to name; it is just worth knowing which one you are saving.
              </>
            ) : (
              <>
                {currentFilters} of them. Close this dialog, change the filters and reopen it to
                save a different set.
              </>
            )}{' '}
            The brand you are working in is <strong>not</strong> part of a view: it lives in the
            shell and follows you across every screen, so a view that carried its own brand could
            disagree with the one at the top of the page.
          </div>
        </div>

        <Field
          label="Name this view"
          error={fields['name']}
          hint={
            clashing
              ? `You already have a view called “${clashing.name}” — saving will replace its filters.`
              : readOnly
                ? 'Yours alone. 60 characters at most.'
                : 'Yours alone unless you share it. 60 characters at most.'
          }
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Failed deposits this week"
            />
          )}
        </Field>

        {readOnly ? (
          <p className="text-subtle" style={{ margin: 0 }}>
            {role} is a read-only role, so a view you save is yours alone: it cannot be shared with
            other staff.
          </p>
        ) : (
          <label className="row" style={{ gap: 'var(--space-2)' }}>
            <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
            <span>
              Share with every admin who can open this screen
              <span className="text-subtle" style={{ display: 'block', fontSize: '11px' }}>
                They can open it; only you can rename, change or remove it. Sharing a view shares
                filters, never rows — each admin still sees only the brands their own access allows.
              </span>
            </span>
          </label>
        )}

        <div className="row">
          <Button variant="primary" busy={busy} disabled={busy} onClick={() => void save()}>
            {clashing ? 'Replace this view' : 'Save this view'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
